// ── Sim Builder — Central Theme ─────────────────────────────────────────────
// All colours live here. To switch modes, just swap which palette is active.

const dark = {
  pageBg:       '#111827',
  headerBg:     '#1e3a5f',
  cardBg:       '#1f2937',
  inputBg:      '#111827',
  modalBg:      '#1f2937',
  borderColor:  '#374151',
  hoverBg:      'rgba(255,255,255,0.07)',
  textPrimary:  '#f9fafb',
  textSecondary:'#cbd5e1', // brightened for AA contrast on dark backgrounds (~10:1)
  textMuted:    '#9ca3af', // brightened so muted text still meets AA (~6:1)
  shadow:       '0 2px 8px rgba(0,0,0,0.5)',
  shadowCard:   '0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(79,70,229,0.08)',
  footerBg:     '#1e3a5f',
  footerText:   '#f9fafb',
  footerBorder: '#111827',
  // Accent colours tuned for use as TEXT on dark backgrounds (AA contrast)
  link:         '#a5b4fc', // indigo-300, ~7.9:1 on cards
  amberText:    '#fbbf24', // amber-400
  successText:  '#4ade80', // green-400
};

const light = {
  pageBg:       '#f0f2f5',
  headerBg:     '#ffffff',
  cardBg:       '#ffffff',
  inputBg:      '#f8fafc',
  modalBg:      '#ffffff',
  borderColor:  '#e2e8f0',
  hoverBg:      'rgba(0,0,0,0.03)',
  textPrimary:  '#1a1a2e',
  textSecondary:'#475569', // ~7.8:1 on white, already AA
  textMuted:    '#64748b', // darkened from #94a3b8 so muted text meets AA (~4.8:1)
  shadow:       '0 2px 8px rgba(0,0,0,0.08)',
  shadowCard:   '0 4px 16px rgba(0,0,0,0.06)',
  footerBg:     '#ffffff',
  footerText:   '#64748b',
  footerBorder: '#e2e8f0',
  // Accent colours tuned for use as TEXT on light backgrounds (AA contrast)
  link:         '#4338ca', // indigo-700, ~7.5:1 on white
  amberText:    '#b45309', // amber-800, ~6:1 on white
  successText:  '#15803d', // green-700, ~4.8:1 on white
};

// Shared across both modes
const shared = {
  indigo:       '#4f46e5',
  indigoHover:  '#4338ca',
  indigoSubtle: 'rgba(79,70,229,0.25)',
  green:        '#22c55e',
  red:          '#ef4444',
  amber:        '#f59e0b',
  blue:         '#0f3460',
  white:        '#ffffff',
};

// ── Exported colours — swap palette here to change mode globally ─────────────
// This is overridden at runtime by ThemeContext, but kept here as a fallback
export const colors = { ...dark, ...shared };

// ── Get a full color set for a given mode ────────────────────────────────────
export function getColors(mode) {
  return { ...(mode === 'light' ? light : dark), ...shared };
}

// ── Build common styles from a color set ─────────────────────────────────────
export function getCommon(c) {
  return {
    page: {
      minHeight: '100vh',
      backgroundColor: c.pageBg,
      color: c.textPrimary,
      fontFamily: 'system-ui, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      backgroundColor: c.headerBg,
      padding: '12px 28px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: c.shadow,
      flexShrink: 0,
      gap: 12,
      borderBottom: `1px solid ${c.borderColor}`,
    },
    headerLeft: {
      display: 'flex', alignItems: 'center', gap: 16, minWidth: 0,
    },
    appTitle: {
      margin: 0, color: c.textPrimary, fontSize: 20,
      fontWeight: 700, letterSpacing: '-0.01em',
    },
    pageHeading: {
      margin: 0, color: c.textPrimary, fontSize: 22, fontWeight: 700,
    },
    sectionHeading: {
      margin: 0, color: c.textPrimary, fontSize: 18, fontWeight: 600,
    },
    card: {
      backgroundColor: c.cardBg, borderRadius: 12,
      border: `1px solid ${c.borderColor}`, boxShadow: c.shadowCard, overflow: 'hidden',
    },
    content: { padding: 32 },
    topBar: {
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', marginBottom: 24,
    },
    empty: {
      textAlign: 'center', padding: '60px 32px', color: c.textSecondary,
      backgroundColor: c.cardBg, borderRadius: 12, border: `1px solid ${c.borderColor}`,
    },
    input: {
      width: '100%', padding: '10px 12px', marginBottom: 16, borderRadius: 8,
      border: `1px solid ${c.borderColor}`, backgroundColor: c.inputBg,
      color: c.textPrimary, fontSize: 14, boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', padding: '10px 12px', marginBottom: 16, borderRadius: 8,
      border: `1px solid ${c.borderColor}`, backgroundColor: c.inputBg,
      color: c.textPrimary, fontSize: 14, boxSizing: 'border-box',
      resize: 'vertical',
    },
    label: {
      display: 'block', color: c.textSecondary,
      fontSize: 13, marginBottom: 6, fontWeight: 500,
    },
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: 20,
    },
    modal: {
      backgroundColor: c.modalBg, border: `1px solid ${c.borderColor}`,
      boxShadow: '0 24px 48px rgba(0,0,0,0.3)', padding: '36px 40px',
      borderRadius: 16, width: '100%', maxWidth: 480,
    },
    modalTitle: {
      margin: '0 0 24px 0', color: c.textPrimary, fontSize: 20, fontWeight: 700,
    },
    modalActions: {
      display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8,
    },
    btn: {
      border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
      fontSize: 14, padding: '9px 18px', transition: 'opacity 0.15s',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    },
    btnPrimary:   { backgroundColor: shared.indigo,  color: shared.white },
    btnSuccess:   { backgroundColor: shared.green,   color: shared.white },
    btnWarning:   { backgroundColor: shared.amber,   color: shared.white },
    btnDanger:    { backgroundColor: shared.red,     color: shared.white },
    btnSecondary: { backgroundColor: shared.blue,    color: shared.white },
    btnGhost: {
      backgroundColor: c.hoverBg, color: c.textPrimary,
      border: `1px solid ${c.borderColor}`,
    },
    errorText: { color: shared.red, fontSize: 13, marginBottom: 12 },
  };
}

// Legacy export so existing pages don't break before being updated
export const common = getCommon(colors);
